import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { api, money, shortDate } from "@/lib/api";
import { StageBadge, Field } from "@/components/common";
import DocumentPanel from "@/components/DocumentPanel";
import NotesPanel from "@/components/NotesPanel";
import { QuarterlyReportsTab } from "@/components/Phase3Tabs";
import { RiskAssessmentTab } from "@/components/RiskAssessmentTab";
import { useAuth } from "@/context/AuthContext";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const TABS = ["Overview", "Funding Summary", "Linked Contracts", "Reporting", "Risk / Compliance", "Documents", "Activity"];

export default function GrantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [g, setG] = useState(null);

  const reload = () => api.get(`/grants/${id}`).then((r) => setG(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  if (!g) return <div className="text-muted-foreground">Loading grant…</div>;

  const pct = g.grant_amount ? Math.min(100, (g.allocated_funds / g.grant_amount) * 100) : 0;

  return (
    <div>
      <button data-testid="back-to-grants" onClick={() => navigate("/grants")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft size={16} /> Grants
      </button>
      <div className="mb-6 rise">
        <div className="overline text-muted-foreground mb-1">{g.funding_source}</div>
        <h1 className="font-head font-bold text-3xl lg:text-4xl tracking-tight leading-none">{g.title}</h1>
        <div className="text-sm text-muted-foreground mt-2">{shortDate(g.grant_period_start)} → {shortDate(g.grant_period_end)} · Manager: {g.grant_manager_name || "—"}</div>
      </div>

      <Tabs defaultValue="Overview">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap no-scrollbar">
          {TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`gtab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="Overview" className="mt-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Total Funding" value={money(g.grant_amount)} />
            <Stat label="Allocated" value={money(g.allocated_funds)} />
            <Stat label="Remaining" value={money(g.remaining_funds)} tone="gold" />
            <Stat label="Linked Contracts" value={g.linked_contract_count} />
          </div>
        </TabsContent>

        <TabsContent value="Funding Summary" className="mt-5">
          <div className="bg-card border border-border rounded-md p-6 max-w-2xl">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Allocated across contracts</span>
              <span className="font-medium">{money(g.allocated_funds)} / {money(g.grant_amount)}</span>
            </div>
            <Progress value={pct} className="h-3" />
            <div className="grid grid-cols-2 gap-5 mt-6">
              <Field label="Current Obligations">{money(g.current_obligations)}</Field>
              <Field label="Remaining Funds">{money(g.remaining_funds)}</Field>
              <Field label="Reporting Obligations">{g.reporting_obligations}</Field>
              <Field label="Risk Level">{g.risk_level}</Field>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="Linked Contracts" className="mt-5">
          {g.linked_contracts?.length === 0 ? (
            <div className="border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground">No contracts linked to this grant.</div>
          ) : (
            <div className="bg-card border border-border rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Contract</TableHead><TableHead>Amount</TableHead><TableHead>Stage</TableHead></TableRow></TableHeader>
                <TableBody>
                  {g.linked_contracts.map((c) => (
                    <TableRow key={c.id} data-testid={`grant-contract-${c.id}`} className="cursor-pointer" onClick={() => navigate(`/contracts/${c.id}`)}>
                      <TableCell><div className="font-medium">{c.title}</div><div className="text-xs text-muted-foreground">{c.contract_number}</div></TableCell>
                      <TableCell className="text-sm">{money(c.amount)}</TableCell>
                      <TableCell><StageBadge stage={c.workflow_stage} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="Reporting" className="mt-5">
          <QuarterlyReportsTab grant={g} canManage={["grant_manager", "admin"].includes(user?.role)} />
        </TabsContent>

        <TabsContent value="Risk / Compliance" className="mt-5">
          <RiskAssessmentTab grant={g} canManage={["grant_manager", "admin"].includes(user?.role)} reload={reload} />
        </TabsContent>

        <TabsContent value="Documents" className="mt-5">
          <div className="bg-card border border-border rounded-md p-6"><DocumentPanel relatedType="grant" relatedId={g.id} /></div>
        </TabsContent>

        <TabsContent value="Activity" className="mt-5">
          <div className="bg-card border border-border rounded-md p-6"><NotesPanel relatedType="grant" relatedId={g.id} /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`bg-card border border-border border-l-4 ${tone === "gold" ? "border-l-gold" : "border-l-primary"} rounded-md p-5`}>
      <div className="overline text-muted-foreground">{label}</div>
      <div className="font-head font-bold text-2xl tracking-tight mt-2">{value}</div>
    </div>
  );
}
